import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import axios from 'axios';
import ApiErrorMessage from '@/shared/components/flash/ApiErrorMessage';
import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';

// Matches GiftcodeVisibilityService::MASK on the backend — used by callers to
// detect whether a fetch already came back unmasked (e.g. verified on another
// tab in the same session) without needing this component to track that itself.
export const GIFTCODE_MASK = '••••';

// The verify endpoint's expires_at only ever lived in React state, so a page
// refresh lost it even though the backend still considered the admin verified
// (initiallyVerified came back true) — the countdown had nothing to count down
// from. Persisting it here lets a refresh restore the actual countdown instead
// of showing "Codes visible for" with a blank time.
const REVEAL_EXPIRES_AT_STORAGE_KEY = 'giftcodeRevealExpiresAt';

const readStoredExpiresAt = () => {
    const stored = localStorage.getItem(REVEAL_EXPIRES_AT_STORAGE_KEY);
    return stored && new Date(stored) > new Date() ? stored : null;
};

const formatCountdown = (seconds) => {
    if (seconds == null) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const GiftCodeRevealGate = ({ initiallyVerified, onVerified, onExpire, theme, rtl }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isVerified, setIsVerified] = useState(initiallyVerified);
    const [expiresAt, setExpiresAt] = useState(readStoredExpiresAt);
    const [secondsRemaining, setSecondsRemaining] = useState(null);
    const [errors, setErrors] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setIsVerified(initiallyVerified);
    }, [initiallyVerified]);

    useEffect(() => {
        if (!isVerified || !expiresAt) return undefined;

        const tick = () => {
            const remaining = Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000));
            setSecondsRemaining(remaining);
            if (remaining <= 0) {
                setIsVerified(false);
                setExpiresAt(null);
                localStorage.removeItem(REVEAL_EXPIRES_AT_STORAGE_KEY);
                onExpire && onExpire();
            }
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [isVerified, expiresAt]);

    const toggle = () => setIsOpen((prev) => !prev);

    const generate = async () => {
        setLoading(true);
        setErrors(null);
        try {
            await axios.post('/giftcode-reveal/generate');
            setIsOpen(true);
        } catch (error) {
            setErrors(error.response?.data);
        } finally {
            setLoading(false);
        }
    };

    const validate = (values) => {
        const validationErrors = {};
        if (!values.code) validationErrors.code = 'Code is required';
        return validationErrors;
    };

    const onSubmit = async (values) => {
        setErrors(null);
        try {
            const response = await axios.post('/giftcode-reveal/verify', { code: values.code });
            setIsVerified(true);
            setExpiresAt(response.data.expires_at);
            localStorage.setItem(REVEAL_EXPIRES_AT_STORAGE_KEY, response.data.expires_at);
            setIsOpen(false);
            onVerified && onVerified(response.data.expires_at);
        } catch (error) {
            setErrors(error.response?.data);
        }
    };

    if (isVerified) {
        return (
            <span className="giftcode-reveal-status text-muted">
                Codes visible for {formatCountdown(secondsRemaining)}
            </span>
        );
    }

    return (
        <>
            <Button color="primary" size="sm" onClick={generate} disabled={loading}>
                {loading ? (
                    <>
                        <span
                            className="spinner-border spinner-border-sm align-middle mr-1"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        Sending code...
                    </>
                ) : (
                    'Reveal Codes'
                )}
            </Button>
            <Modal
                className={`modal-giftcode-reveal ${theme.className} ${rtl.direction}-support`}
                isOpen={isOpen}
                toggle={toggle}
            >
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    render={({ handleSubmit }) => (
                        <form className="form" onSubmit={handleSubmit}>
                            <ModalHeader toggle={toggle}>Verify to Reveal Codes</ModalHeader>
                            <ModalBody>
                                {errors && <ApiErrorMessage errors={errors} />}
                                <p>A verification code has been sent to your email.</p>
                                <Field name="code">
                                    {({ input, meta }) => (
                                        <div className="form__form-group">
                                            <div className="form__form-group-field">
                                                <div className="form__form-group-row">
                                                    <input type="text" {...input} placeholder="Enter 2FA code" autoFocus />
                                                    {meta.touched && meta.error && (
                                                        <span className="form__form-group-error">{meta.error}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Field>
                                <Button type="submit" color="primary" className="mt-2" disabled={loading}>Verify</Button>
                                <div className="resend-btn mt-2">
                                    {loading ? (
                                        <span className="text-muted">
                                            <span
                                                className="spinner-border spinner-border-sm align-middle mr-1"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Sending code...
                                        </span>
                                    ) : (
                                        <a onClick={generate}>Resend Code</a>
                                    )}
                                </div>
                            </ModalBody>
                        </form>
                    )}
                />
            </Modal>
        </>
    );
};

GiftCodeRevealGate.propTypes = {
    theme: ThemeProps.isRequired,
    rtl: RTLProps.isRequired,
    initiallyVerified: PropTypes.bool,
    onVerified: PropTypes.func,
    onExpire: PropTypes.func,
};

GiftCodeRevealGate.defaultProps = {
    initiallyVerified: false,
    onVerified: null,
    onExpire: null,
};

export default withRouter(connect((state) => ({
    theme: state.theme,
    rtl: state.rtl,
}))(GiftCodeRevealGate));
